import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import type {
  NormalizedIssue,
  NormalizedComment,
  LinearCatalogData,
  LinearWorkflowStateSummary,
  LinearUserSummary,
  LinearProjectSummary,
  LinearIssueCommentsData,
} from '@corner/shared';

// ─── Props ──────────────────────────────────────

interface LinearEditIssueModalProps {
  issueId: string;
  onClose: () => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
  getToken?: () => string | null;
}

// ─── Priority definitions ───────────────────────

const PRIORITIES = [
  { value: 0, label: 'No priority' },
  { value: 4, label: 'Urgent' },
  { value: 1, label: 'High' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'Low' },
] as const;

function priorityLabel(value: number) {
  return PRIORITIES.find((p) => p.value === value)?.label ?? 'No priority';
}

// ─── State color map ────────────────────────────

function stateColor(type?: string | null): string {
  switch (type) {
    case 'backlog': return '#B8ACA2';
    case 'unstarted': return '#E2A746';
    case 'started': return '#D97645';
    case 'completed': return '#1B7A4B';
    case 'cancelled': return '#9A8E82';
    default: return '#B8ACA2';
  }
}

// ─── Icons ──────────────────────────────────────

function LinearLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1.225 61.523c-.2225-.9485.907-1.546 1.596-.857L39.334 97.178c.6889.689.091 1.819-.857 1.596C20.052 94.452 5.548 79.948 1.225 61.523Z" fill="#5E6AD2" />
      <path d="M.00189 46.889c-.1764.283.889.56.29.761L52.35 99.709c.2007.201.477.307.761.29 2.369-.1476 4.694-.46 6.962-.9259.764-.157 1.03-1.096.478-1.648L2.576 39.449c-.5519-.5519-1.491-.2863-1.648.478-.4659 2.269-.7783 4.593-.92588 6.962Z" fill="#5E6AD2" />
      <path d="M4.211 29.705c-.1665.374-.0817.811.208 1.1l64.776 64.776c.2894.289.726.374 1.1.208 1.786-.7956 3.517-1.693 5.186-2.684.552-.328.637-1.087.183-1.541L8.436 24.337c-.4541-.4541-1.213-.3689-1.541.183-.9913 1.668-1.888 3.399-2.684 5.186Z" fill="#5E6AD2" />
      <path d="M12.659 18.074c-.3701-.3701-.393-.9637-.0443-1.354C21.779 6.459 35.111 0 49.952 0 77.593 0 100 22.407 100 50.048c0 14.841-6.459 28.172-16.72 37.337-.3903.349-.984.326-1.354-.0443L12.659 18.074Z" fill="#5E6AD2" />
    </svg>
  );
}

function PriorityIcon({ priority, color }: { priority: number; color: string }) {
  const bars = priority === 0 ? 0 : priority === 4 ? 3 : priority === 1 ? 3 : priority === 2 ? 2 : 1;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <rect x="1" y="3" width="2.5" height="8" rx="0.8" fill={color} opacity={bars >= 3 ? 1 : 0.2} />
      <rect x="4.5" y="5.5" width="2.5" height="5.5" rx="0.8" fill={color} opacity={bars >= 2 ? 1 : 0.2} />
      <rect x="8" y="8" width="2.5" height="3" rx="0.8" fill={color} opacity={bars >= 1 ? 1 : 0.2} />
    </svg>
  );
}

function StatusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7.5" cy="7.5" r="6" stroke="#9A8E82" strokeWidth="1.2" />
      <circle cx="7.5" cy="7.5" r="3" fill="#9A8E82" />
    </svg>
  );
}

function PriorityLabelIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <rect x="2" y="3" width="3" height="9" rx="1" fill="#9A8E82" />
      <rect x="6" y="6" width="3" height="6" rx="1" fill="#9A8E82" opacity="0.5" />
      <rect x="10" y="9" width="3" height="3" rx="1" fill="#9A8E82" opacity="0.3" />
    </svg>
  );
}

function AssigneeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7.5" cy="5.5" r="2.5" stroke="#9A8E82" strokeWidth="1.2" />
      <path d="M3 13C3 10.5 5 9 7.5 9C10 9 12 10.5 12 13" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ProjectIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <rect x="2" y="2" width="11" height="11" rx="2" stroke="#9A8E82" strokeWidth="1.2" />
      <path d="M5 6H10M5 9H8" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2.5 3.75L5 6.25L7.5 3.75" stroke="#9A8E82" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function Checkmark() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 7.5l3 3 5-6" stroke="#5E6AD2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0].toUpperCase();
}

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Main component ─────────────────────────────

export default function LinearEditIssueModal({
  issueId,
  onClose,
  onUpdated,
  onDeleted,
  getToken,
}: LinearEditIssueModalProps) {
  // Data
  const [issue, setIssue] = useState<NormalizedIssue | null>(null);
  const [comments, setComments] = useState<NormalizedComment[]>([]);
  const [catalog, setCatalog] = useState<LinearCatalogData | null>(null);
  const [loading, setLoading] = useState(true);

  // Editable fields
  const [editTitle, setEditTitle] = useState('');
  const [selectedState, setSelectedState] = useState<LinearWorkflowStateSummary | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<number>(0);
  const [selectedAssignee, setSelectedAssignee] = useState<LinearUserSummary | null>(null);
  const [selectedProject, setSelectedProject] = useState<LinearProjectSummary | null>(null);

  // UI state
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  // Track which team the issue belongs to (from identifier prefix or catalog match)
  const issueTeamName = issue?.team ?? null;
  const commentInputRef = useRef<HTMLInputElement>(null);

  const headers = useCallback(() => {
    const token = getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  // Fetch issue + comments + catalog in parallel
  useEffect(() => {
    setLoading(true);
    const h = headers();
    Promise.all([
      axios.get<NormalizedIssue>(`/api/integrations/linear/issues/${issueId}`, { headers: h }),
      axios.get<LinearIssueCommentsData>(`/api/integrations/linear/issues/${issueId}/comments`, { headers: h }),
      axios.get<LinearCatalogData>('/api/integrations/linear/catalog', { headers: h }),
    ])
      .then(([issueRes, commentsRes, catalogRes]) => {
        const iss = issueRes.data;
        setIssue(iss);
        setEditTitle(iss.title);
        setComments(commentsRes.data.results ?? []);
        const cat = catalogRes.data;
        setCatalog(cat);

        // Match current state from catalog
        if (iss.status && cat.states.length > 0) {
          const match = cat.states.find((s) => s.name === iss.status);
          if (match) setSelectedState(match);
        }

        // Match assignee
        if (iss.assignee && cat.assignees.length > 0) {
          const match = cat.assignees.find((a) => a.name === iss.assignee);
          if (match) setSelectedAssignee(match);
        }

        // Match project
        if (iss.project && cat.projects.length > 0) {
          const match = cat.projects.find((p) => p.name === iss.project);
          if (match) setSelectedProject(match);
        }

        // Priority — we don't get numeric priority from NormalizedIssue, default to 0
        // The issue data doesn't include raw priority number, keep at 0 for now
      })
      .catch(() => {
        toast({ title: 'Failed to load', description: 'Could not fetch issue details.', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [issueId, headers]);

  // Team-scoped states and projects
  const issueTeam = useMemo(() => {
    if (!catalog || !issueTeamName) return null;
    return catalog.teams.find((t) => t.name === issueTeamName) ?? null;
  }, [catalog, issueTeamName]);

  const teamStates = useMemo(() => {
    if (!catalog) return [];
    if (issueTeam) return catalog.states.filter((s) => s.teamId === issueTeam.id || !s.teamId);
    return catalog.states;
  }, [catalog, issueTeam]);

  const teamProjects = useMemo(() => {
    if (!catalog) return [];
    if (issueTeam) return catalog.projects.filter((p) => p.teamIds.includes(issueTeam.id));
    return catalog.projects;
  }, [catalog, issueTeam]);

  const activeAssignees = useMemo(() => {
    if (!catalog) return [];
    return catalog.assignees.filter((a) => a.active !== false);
  }, [catalog]);

  // Track the original values so we can diff
  const originalRef = useRef<{
    title: string;
    stateName: string | null;
    assigneeName: string | null;
    projectName: string | null;
  }>({ title: '', stateName: null, assigneeName: null, projectName: null });

  // Set originals when issue loads
  useEffect(() => {
    if (issue) {
      originalRef.current = {
        title: issue.title,
        stateName: issue.status ?? null,
        assigneeName: issue.assignee ?? null,
        projectName: issue.project ?? null,
      };
    }
  }, [issue]);

  // Save changes
  const handleSave = useCallback(async () => {
    if (!issue) return;
    setSaving(true);
    try {
      const orig = originalRef.current;
      const payload: Record<string, unknown> = {};

      if (editTitle.trim() !== orig.title) payload.title = editTitle.trim();
      if (selectedState && selectedState.name !== orig.stateName) payload.stateId = selectedState.id;
      if (selectedPriority !== 0) payload.priority = selectedPriority;

      // Assignee: only send if changing TO a different user (unassign not supported via API yet)
      if (selectedAssignee && selectedAssignee.name !== orig.assigneeName) {
        payload.assigneeId = selectedAssignee.id;
      }

      // Project: only send if changing TO a different project (remove not supported via API yet)
      if (selectedProject && selectedProject.name !== orig.projectName) {
        payload.projectId = selectedProject.id;
      }

      // Only call API if there are actual changes
      if (Object.keys(payload).length > 0) {
        await axios.patch(`/api/integrations/linear/issues/${issueId}`, payload, { headers: headers() });
      }

      toast({ title: 'Issue updated', description: `"${editTitle.trim()}" has been saved.` });
      onUpdated?.();
      onClose();
    } catch {
      toast({ title: 'Failed to save', description: 'Could not update the issue.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [issue, editTitle, selectedState, selectedPriority, selectedAssignee, selectedProject, issueId, headers, onUpdated, onClose]);

  // Delete issue
  const handleDelete = useCallback(async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`/api/integrations/linear/issues/${issueId}`, { headers: headers() });
      toast({ title: 'Issue deleted', description: 'The Linear issue has been removed.' });
      onDeleted?.();
      onClose();
    } catch {
      toast({ title: 'Failed to delete', description: 'Could not delete the issue.', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [confirmDelete, issueId, headers, onDeleted, onClose]);

  // Add comment
  const handleAddComment = useCallback(async () => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      await axios.post(`/api/integrations/linear/issues/${issueId}/comments`, { body: commentText.trim() }, { headers: headers() });
      // Append optimistically
      setComments((prev) => [...prev, {
        provider: 'linear',
        itemType: 'comment',
        id: `temp-${Date.now()}`,
        body: commentText.trim(),
        author: 'You',
        createdAt: new Date().toISOString(),
      }]);
      setCommentText('');
      toast({ title: 'Comment added' });
    } catch {
      toast({ title: 'Failed', description: 'Could not add comment.', variant: 'destructive' });
    } finally {
      setSendingComment(false);
    }
  }, [commentText, issueId, headers]);

  // Open in Linear
  const handleOpenInLinear = useCallback(() => {
    if (issue?.url) window.open(issue.url, '_blank', 'noopener');
  }, [issue]);

  const toggleDropdown = useCallback((name: string) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  }, []);

  return (
    <div style={MODAL_SHELL} onClick={() => { setOpenDropdown(null); setConfirmDelete(false); }}>
      {/* Header */}
      <div style={HEADER}>
        <LinearLogo />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexGrow: 1 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#2E2318', lineHeight: '18px' }}>Edit issue</span>
          {issue?.identifier && (
            <span style={IDENTIFIER_BADGE}>{issue.identifier}</span>
          )}
        </div>
        <button type="button" onClick={onClose} style={CLOSE_BTN}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#9A8E82" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div style={{ width: '100%', height: 1, backgroundColor: '#EDE8E2', flexShrink: 0 }} />

      {loading ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 13, color: '#9A8E82', fontFamily: F }}>Loading issue...</div>
      ) : !issue ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 13, color: '#9A8E82', fontFamily: F }}>Issue not found.</div>
      ) : (
        <>
          {/* Team badge + Title */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '16px 24px 0', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={TEAM_BADGE_SM}>
                <span style={{ fontSize: 8, fontWeight: 700, color: '#FFFFFF' }}>
                  {issueTeam?.key?.[0] ?? issueTeamName?.[0] ?? '?'}
                </span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#9A8E82', fontFamily: F }}>{issueTeamName ?? 'Unknown'}</span>
            </div>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={TITLE_INPUT}
              placeholder="Issue title"
            />
          </div>

          {/* Properties */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 24px 8px', gap: 8 }}>
            <span style={LABEL}>Properties</span>
            <div style={{ flexGrow: 1, height: 1, backgroundColor: '#EDE8E2' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px', gap: 8 }}>
            {/* Status */}
            <PropRow icon={<StatusIcon />} label="Status">
              <div style={{ position: 'relative', flex: 1 }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); toggleDropdown('status'); }} style={PROP_VALUE_FILLED}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: stateColor(selectedState?.type), flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{selectedState?.name ?? issue.status ?? 'Unknown'}</span>
                  <ChevronDown />
                </button>
                {openDropdown === 'status' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    {teamStates.map((s) => (
                      <button key={s.id} type="button" onClick={() => { setSelectedState(s); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: s.id === selectedState?.id ? 500 : 400 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: stateColor(s.type) }} />
                        <span style={{ flex: 1 }}>{s.name}</span>
                        {s.id === selectedState?.id && <Checkmark />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </PropRow>

            {/* Priority */}
            <PropRow icon={<PriorityLabelIcon />} label="Priority">
              <div style={{ position: 'relative', flex: 1 }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); toggleDropdown('priority'); }} style={PROP_VALUE_FILLED}>
                  <PriorityIcon priority={selectedPriority} color="#D97645" />
                  <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{priorityLabel(selectedPriority)}</span>
                  <ChevronDown />
                </button>
                {openDropdown === 'priority' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    {PRIORITIES.map((p) => (
                      <button key={p.value} type="button" onClick={() => { setSelectedPriority(p.value); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: p.value === selectedPriority ? 500 : 400 }}>
                        <PriorityIcon priority={p.value} color={p.value === selectedPriority ? '#D97645' : '#9A8E82'} />
                        <span style={{ flex: 1 }}>{p.label}</span>
                        {p.value === selectedPriority && <Checkmark />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </PropRow>

            {/* Assignee */}
            <PropRow icon={<AssigneeIcon />} label="Assignee">
              <div style={{ position: 'relative', flex: 1 }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); toggleDropdown('assignee'); }} style={selectedAssignee ? PROP_VALUE_FILLED : PROP_VALUE_EMPTY}>
                  {selectedAssignee ? (
                    <>
                      <div style={AVATAR}><span style={{ fontSize: 9, fontWeight: 600, color: '#FFFFFF' }}>{initials(selectedAssignee.name)}</span></div>
                      <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{selectedAssignee.name}</span>
                      <ChevronDown />
                    </>
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 6h6" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round" /></svg>
                      <span style={{ fontSize: 13, color: '#B8ACA2' }}>Assign</span>
                    </>
                  )}
                </button>
                {openDropdown === 'assignee' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => { setSelectedAssignee(null); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, color: '#9A8E82' }}>
                      <span style={{ flex: 1 }}>Unassigned</span>
                      {!selectedAssignee && <Checkmark />}
                    </button>
                    {activeAssignees.map((a) => (
                      <button key={a.id} type="button" onClick={() => { setSelectedAssignee(a); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: a.id === selectedAssignee?.id ? 500 : 400 }}>
                        <div style={{ ...AVATAR, width: 18, height: 18 }}><span style={{ fontSize: 8, fontWeight: 600, color: '#FFFFFF' }}>{initials(a.name)}</span></div>
                        <span style={{ flex: 1 }}>{a.name ?? a.email ?? 'Unknown'}</span>
                        {a.id === selectedAssignee?.id && <Checkmark />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </PropRow>

            {/* Project */}
            <PropRow icon={<ProjectIcon />} label="Project">
              <div style={{ position: 'relative', flex: 1 }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); toggleDropdown('project'); }} style={selectedProject ? PROP_VALUE_FILLED : PROP_VALUE_EMPTY}>
                  {selectedProject ? (
                    <>
                      <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#1B7A4B', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{selectedProject.name}</span>
                      <ChevronDown />
                    </>
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 6h6" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round" /></svg>
                      <span style={{ fontSize: 13, color: '#B8ACA2' }}>Set project</span>
                    </>
                  )}
                </button>
                {openDropdown === 'project' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => { setSelectedProject(null); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, color: '#9A8E82' }}>
                      <span style={{ flex: 1 }}>No project</span>
                      {!selectedProject && <Checkmark />}
                    </button>
                    {teamProjects.map((p) => (
                      <button key={p.id} type="button" onClick={() => { setSelectedProject(p); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: p.id === selectedProject?.id ? 500 : 400 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#1B7A4B', flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{p.name}</span>
                        {p.id === selectedProject?.id && <Checkmark />}
                      </button>
                    ))}
                    {teamProjects.length === 0 && (
                      <div style={{ padding: '8px 12px', fontSize: 12, color: '#9A8E82', fontFamily: F }}>No projects for this team</div>
                    )}
                  </div>
                )}
              </div>
            </PropRow>
          </div>

          {/* Activity section */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '16px 24px 8px', gap: 8 }}>
            <span style={LABEL}>Activity</span>
            <span style={{ fontSize: 11, fontWeight: 400, color: '#B8ACA2', fontFamily: F }}>
              {comments.length} comment{comments.length !== 1 ? 's' : ''}
            </span>
            <div style={{ flexGrow: 1, height: 1, backgroundColor: '#EDE8E2' }} />
          </div>

          {/* Comments list */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px', gap: 12 }}>
            {comments.map((c) => (
              <div key={c.id} style={{ display: 'flex', gap: 10 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  backgroundColor: c.author === 'Corner AI' || c.author === 'You' ? '#D97645' : '#5E6AD2',
                }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: '#FFFFFF' }}>
                    {c.author === 'Corner AI' ? 'AI' : initials(c.author)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#2E2318', fontFamily: F }}>{c.author ?? 'Unknown'}</span>
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#B8ACA2', fontFamily: F }}>{timeAgo(c.createdAt)}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 400, color: '#6B5E52', lineHeight: '18px', fontFamily: F }}>{c.body}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Add comment input */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 24px', marginTop: 8 }}>
            <div style={COMMENT_INPUT_WRAP}>
              <input
                ref={commentInputRef}
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !sendingComment) handleAddComment(); }}
                style={COMMENT_INPUT}
                placeholder="Add a comment..."
                disabled={sendingComment}
              />
              {commentText.trim() && (
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={sendingComment}
                  style={COMMENT_SEND}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M12 7L2 2L4 7L2 12L12 7Z" fill="#5E6AD2" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div style={FOOTER}>
            {/* Open in Linear */}
            <button type="button" onClick={handleOpenInLinear} style={OPEN_LINEAR_BTN}>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                <path d="M5.5 1.5H2C1.72 1.5 1.5 1.72 1.5 2V5.5L7.5 11.5L11.5 7.5L5.5 1.5Z" stroke="#5E6AD2" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#5E6AD2', fontFamily: F }}>Open in Linear</span>
            </button>

            <div style={{ flexGrow: 1 }} />

            {/* Delete */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              disabled={deleting}
              style={DELETE_BTN}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                <path d="M2 3.5H11M5 3.5V2C5 1.72 5.22 1.5 5.5 1.5H7.5C7.78 1.5 8 1.72 8 2V3.5M3.5 3.5V11C3.5 11.28 3.72 11.5 4 11.5H9C9.28 11.5 9.5 11.28 9.5 11V3.5" stroke="#E8534A" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#E8534A', fontFamily: F }}>
                {confirmDelete ? 'Confirm?' : deleting ? 'Deleting...' : 'Delete'}
              </span>
            </button>

            {/* Save */}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !editTitle.trim()}
              style={{ ...SAVE_BTN, opacity: saving || !editTitle.trim() ? 0.5 : 1 }}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────

function PropRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div style={PROP_ROW}>
      <div style={PROP_LABEL_COL}>
        {icon}
        <span style={{ fontSize: 13, color: '#6B5E52', lineHeight: '16px', fontFamily: F }}>{label}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────

const F = '"Geist", system-ui, sans-serif';

const MODAL_SHELL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 460,
  maxHeight: '80vh',
  borderRadius: 14,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#FDFCFB',
  boxShadow: 'rgba(0,0,0,0.25) 0px 16px 60px, rgba(0,0,0,0.05) 0px 0px 0px 1px',
  fontFamily: F,
};

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '20px 24px 16px',
  gap: 10,
};

const CLOSE_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
};

const IDENTIFIER_BADGE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: '#5E6AD2',
  backgroundColor: 'rgba(94, 106, 210, 0.1)',
  padding: '2px 8px',
  borderRadius: 4,
  fontFamily: F,
  lineHeight: '16px',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#9A8E82',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: '14px',
  fontFamily: F,
};

const TEAM_BADGE_SM: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 3,
  backgroundColor: '#5E6AD2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const TITLE_INPUT: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #EDE8E2',
  backgroundColor: '#FFFFFF',
  fontSize: 14,
  fontWeight: 500,
  color: '#2E2318',
  fontFamily: F,
  lineHeight: '18px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const DROPDOWN: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  maxHeight: 200,
  overflowY: 'auto',
  backgroundColor: '#FDFCFB',
  border: '1px solid #E4DDD4',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  zIndex: 10,
  padding: 4,
};

const DROPDOWN_ITEM: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  borderRadius: 6,
  fontFamily: F,
  fontSize: 13,
  color: '#2E2318',
  textAlign: 'left',
};

const PROP_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: 36,
  gap: 0,
};

const PROP_LABEL_COL: React.CSSProperties = {
  width: 110,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const PROP_VALUE_FILLED: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  padding: '6px 10px',
  borderRadius: 6,
  backgroundColor: '#F5F2ED',
  border: 'none',
  cursor: 'pointer',
  fontFamily: F,
  textAlign: 'left',
  gap: 6,
};

const PROP_VALUE_EMPTY: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px dashed #D4CAC0',
  background: 'none',
  gap: 6,
  cursor: 'pointer',
  fontFamily: F,
};

const AVATAR: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: '#5E6AD2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const COMMENT_INPUT_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  borderRadius: 8,
  border: '1px solid #EDE8E2',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
};

const COMMENT_INPUT: React.CSSProperties = {
  flex: 1,
  padding: '8px 12px',
  border: 'none',
  outline: 'none',
  fontSize: 13,
  fontWeight: 400,
  color: '#2E2318',
  fontFamily: F,
  lineHeight: '16px',
  backgroundColor: 'transparent',
};

const COMMENT_SEND: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const FOOTER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '16px 24px 20px',
  gap: 10,
};

const OPEN_LINEAR_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily: F,
};

const DELETE_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '6px 10px',
  borderRadius: 6,
  fontFamily: F,
};

const SAVE_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 20px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#2E2318',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  color: '#FDFCFB',
  fontFamily: F,
  lineHeight: '18px',
};
